import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"
import 'tom-select/dist/css/tom-select.css'
import '../stylesheets/tom-select.scss'

export default class extends Controller {
  static targets = ["select"]
  static values = {
    url: String,
    profileId: String
  }

  connect() {
    // Nur TomSelect initialisieren wenn ein Select-Target vorhanden ist
    if (this.hasSelectTarget) {
      this.initializeTomSelect()
    }
  }

  updateHighlightStar(event) {
    const checkbox = event.currentTarget
    // Finde den Stern-Icon im gleichen Label
    const label = checkbox.closest('label')
    if (!label) return
    
    const starIcon = label.querySelector('i.fa-star, i.fas.fa-star, i.far.fa-star')
    if (!starIcon) return
    
    // Aktualisiere den Stern basierend auf Checkbox-Status
    if (checkbox.checked) {
      starIcon.classList.remove('far', 'text-gray-300')
      starIcon.classList.add('fas', 'text-yellow-500')
    } else {
      starIcon.classList.remove('fas', 'text-yellow-500')
      starIcon.classList.add('far', 'text-gray-300')
    }
    
    // Speichere automatisch nach Highlight-Änderung
    this.saveProfileCompetencies()
  }
  
  saveProfileCompetencies() {
    // Prüfe ob Profile-ID vorhanden ist
    const profileId = this.profileIdValue
    if (!profileId || profileId === 'new') {
      return
    }

    // Finde das Formular
    const form = document.querySelector('form[action*="/backend/profiles"]')
    if (!form) {
      console.error('Formular nicht gefunden')
      return
    }

    // Debounce: Warte 500ms bevor gespeichert wird
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    
    this.saveTimeout = setTimeout(() => {
      // Erstelle FormData aus dem Formular
      const formData = new FormData(form)
      
      // Füge live_update Flag hinzu
      formData.append('live_update', 'true')
      
      // Add method override for PATCH
      const method = formData.get("_method") || form.method || "POST"
      if (method !== "POST") {
        formData.set("_method", method)
      }

      // Verwende fetch mit Turbo Stream Headers (ohne Page Reload)
      fetch(form.action || form.getAttribute('action'), {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || "",
          "Accept": "text/vnd.turbo-stream.html, text/html, application/xhtml+xml",
        },
        credentials: "same-origin",
      })
      .then((response) => {
        if (response.ok) {
          return response.text()
        } else {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      })
      .then((html) => {
        // Process Turbo Stream response
        if (html.includes("turbo-stream")) {
          Turbo.renderStreamMessage(html)
        }
        
        // Benachrichtige den Preview-Controller
        document.dispatchEvent(new CustomEvent('profile:competencies-updated'))
      })
      .catch((error) => {
        console.error("Fehler beim Speichern der Kompetenzen:", error)
      })
    }, 500)
  }

  initializeTomSelect() {
    if (!this.hasSelectTarget) return;

    this.tomselect = new TomSelect(this.selectTarget, {
      valueField: 'id',
      labelField: 'text',
      searchField: ['text'],
      placeholder: "Kompetenz suchen oder neue erstellen...",
      openOnFocus: true,
      preload: 'focus',
      closeAfterSelect: true,
      selectOnTab: true,
      maxOptions: 100,
      create: true,
      createOnBlur: false,
      createFilter: (input) => {
        return input.length >= 2; // Mindestens 2 Zeichen für neue Kompetenz
      },
      create: (input) => {
        return {
          id: 'new_' + input,
          text: `"${input}" erstellen`,
          isNew: true
        };
      },
      load: (query, callback) => {
        if (!query.length) return callback();
        
        // Verwende die Backend-Such-API
        const url = `/de/backend/search/?model=competencies&term=${encodeURIComponent(query)}`
        
        fetch(url)
          .then(response => response.json())
          .then(json => {
            // Prüfe auf ähnliche Kompetenzen für Warnung
            const similarCompetencies = json.filter(item => 
              item.text.toLowerCase().includes(query.toLowerCase()) ||
              query.toLowerCase().includes(item.text.toLowerCase())
            );
            
            if (similarCompetencies.length > 0 && query.length >= 3) {
              // Füge Warnung hinzu
              json.unshift({
                id: 'warning',
                text: `⚠️ Ähnliche Kompetenzen gefunden: ${similarCompetencies.map(c => c.text).join(', ')}`,
                disabled: true
              });
            }
            
            callback(json)
          }).catch(() => {
            callback()
          })
      },
      render: {
        no_results: function(data, escape) {
          return '<div class="no-results">Keine Ergebnisse für "' + escape(data.input) + '"</div>';
        },
        option_create: function(data, escape) {
          return '<div class="create">Hinzuf&uuml;gen: <strong>' + escape(data.input) + '</strong>&hellip;</div>';
        },
        option: function(item, escape) {
          if (item.isNew) {
            return `<div class="option text-green-600">
              <span class="title text-sm font-medium">${escape(item.text)}</span>
              <span class="subtext text-xs text-green-500">Neue Kompetenz erstellen</span>
            </div>`
          } else if (item.id === 'warning') {
            return `<div class="option text-yellow-600 bg-yellow-50">
              <span class="title text-sm">${escape(item.text)}</span>
            </div>`
          } else {
            return `<div class="option">
              <span class="title text-sm">${escape(item.text)}</span>
              ${item.subtext ? `<span class="subtext text-xs text-gray-500">${item.subtext}</span>` : ''}
            </div>`
          }
        },
        item: function(item, escape) {
          if (item.isNew) {
            return `<div class="item text-sm text-green-600">${escape(item.text.replace(' erstellen', ''))}</div>`
          } else {
            return `<div class="item text-sm">${escape(item.text)}</div>`
          }
        }
      },
      onChange: (value) => {
        if (value) {
          this.addCompetency(value);
        }
      }
    })
  }

  addCompetency(competencyId) {
    const profileId = this.selectTarget.dataset.profileId;
    
    if (!profileId || profileId === 'new') {
      this.showError('Bitte speichern Sie das Profil zuerst, bevor Sie Kompetenzen hinzufügen.');
      this.tomselect.clear();
      return;
    }
    
    // Prüfe ob es eine neue Kompetenz ist
    if (competencyId.startsWith('new_')) {
      const newCompetencyTitle = competencyId.replace('new_', '');
      this.createCompetency(newCompetencyTitle, profileId);
    } else {
      // Verwende bestehende Kompetenz
      this.addCompetencyToProfile(competencyId, profileId);
    }
  }

  createCompetency(title, profileId) {
    const url = '/de/backend/competencies';
    const formData = new FormData();
    formData.append('competency[title]', title);
    // Für Skills: immer Tenant-Katalog verwenden
    formData.append('competency_type', 'skill');
    
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Jetzt füge die Kompetenz zum Profil hinzu
        this.addCompetencyToProfile(data.competency_id, profileId);
      } else {
        this.showError('Fehler beim Erstellen der Kompetenz: ' + (data.errors || 'Unbekannter Fehler'));
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Erstellen der Kompetenz');
    });
  }

  addCompetencyToProfile(applicantCompetencyId, profileId, competencyId) {
    const url = `/de/backend/profiles/${profileId}/profile_competencies`;
    const formData = new FormData();
    formData.append('profile_competency[applicant_competency_id]', applicantCompetencyId);
    formData.append('profile_competency[highlight]', 'false'); // Standard-Hervorhebung

    this.sendRequest(url, formData, competencyId);
  }

  toggleCompetency(event) {
    const checkbox = event.currentTarget;
    const competencyId = checkbox.dataset.competencyId;
    const applicantCompetencyId = checkbox.dataset.applicantCompetencyId;
    const profileId = this.profileIdValue || this.selectTarget?.dataset.profileId;
    
    if (!profileId || profileId === 'new') {
      this.showError('Bitte speichern Sie das Profil zuerst, bevor Sie Kompetenzen hinzufügen.');
      checkbox.checked = false;
      return;
    }
    
    // Finde das Kompetenz-Element
    const competencyElement = document.querySelector(`[data-competency-id="${competencyId}"]`)
    if (!competencyElement) return
    
    const profileCompetencyId = competencyElement.dataset.profileCompetencyId
    
    // Finde das Formular, um Hidden Fields dort hinzuzufügen
    const form = document.querySelector('form[action*="/backend/profiles"]')
    if (!form) {
      console.error('Formular nicht gefunden')
      return
    }
    
    if (checkbox.checked) {
      // Kompetenz aktivieren: Erstelle/aktualisiere Hidden Fields
      if (profileCompetencyId && profileCompetencyId !== '') {
        // Bestehende Kompetenz: Stelle sicher, dass Hidden Fields vorhanden sind
        let idInput = form.querySelector(`input[name="profile[profile_competencies_attributes][${profileCompetencyId}][id]"]`)
        let applicantIdInput = form.querySelector(`input[name="profile[profile_competencies_attributes][${profileCompetencyId}][applicant_competency_id]"]`)
        let destroyInput = form.querySelector(`input[name="profile[profile_competencies_attributes][${profileCompetencyId}][_destroy]"]`)
        
        // Entferne _destroy Input falls vorhanden
        if (destroyInput) {
          destroyInput.remove()
        }
        
        if (!idInput) {
          idInput = document.createElement('input')
          idInput.type = 'hidden'
          idInput.name = `profile[profile_competencies_attributes][${profileCompetencyId}][id]`
          idInput.value = profileCompetencyId
          form.appendChild(idInput)
        }
        
        if (!applicantIdInput) {
          applicantIdInput = document.createElement('input')
          applicantIdInput.type = 'hidden'
          applicantIdInput.name = `profile[profile_competencies_attributes][${profileCompetencyId}][applicant_competency_id]`
          applicantIdInput.value = applicantCompetencyId
          form.appendChild(applicantIdInput)
        }
        
        // Stelle sicher, dass Position-Input vorhanden ist
        let positionInput = form.querySelector(`input[name="profile[profile_competencies_attributes][${profileCompetencyId}][position]"]`)
        if (!positionInput) {
          positionInput = document.createElement('input')
          positionInput.type = 'hidden'
          positionInput.className = 'position-input'
          positionInput.name = `profile[profile_competencies_attributes][${profileCompetencyId}][position]`
          positionInput.value = '0'
          form.appendChild(positionInput)
        }
      } else {
        // Neue Kompetenz: Erstelle Hidden Fields für neue Kompetenz
        let applicantIdInput = form.querySelector(`input[name="profile[profile_competencies_attributes][new_${applicantCompetencyId}][applicant_competency_id]"]`)
        if (!applicantIdInput) {
          applicantIdInput = document.createElement('input')
          applicantIdInput.type = 'hidden'
          applicantIdInput.name = `profile[profile_competencies_attributes][new_${applicantCompetencyId}][applicant_competency_id]`
          applicantIdInput.value = applicantCompetencyId
          form.appendChild(applicantIdInput)
        }
        
        // Stelle sicher, dass Position-Input vorhanden ist
        let positionInput = form.querySelector(`input[name="profile[profile_competencies_attributes][new_${applicantCompetencyId}][position]"]`)
        if (!positionInput) {
          positionInput = document.createElement('input')
          positionInput.type = 'hidden'
          positionInput.className = 'position-input'
          positionInput.name = `profile[profile_competencies_attributes][new_${applicantCompetencyId}][position]`
          positionInput.value = '0'
          form.appendChild(positionInput)
        }
        
        // Stelle sicher, dass Highlight-Input vorhanden ist
        let highlightInput = form.querySelector(`input[name="profile[profile_competencies_attributes][new_${applicantCompetencyId}][highlight]"]`)
        if (!highlightInput) {
          highlightInput = document.createElement('input')
          highlightInput.type = 'hidden'
          highlightInput.name = `profile[profile_competencies_attributes][new_${applicantCompetencyId}][highlight]`
          highlightInput.value = 'false'
          form.appendChild(highlightInput)
        }
      }
    } else {
      // Kompetenz deaktivieren: Markiere für Löschung
      if (profileCompetencyId && profileCompetencyId !== '') {
        let destroyInput = form.querySelector(`input[name="profile[profile_competencies_attributes][${profileCompetencyId}][_destroy]"]`)
        if (!destroyInput) {
          destroyInput = document.createElement('input')
          destroyInput.type = 'hidden'
          destroyInput.name = `profile[profile_competencies_attributes][${profileCompetencyId}][_destroy]`
          destroyInput.value = '1'
          form.appendChild(destroyInput)
        }
      } else {
        // Neue Kompetenz: Entferne Hidden Fields
        const newInputs = form.querySelectorAll(`input[name*="profile[profile_competencies_attributes][new_${applicantCompetencyId}]"]`)
        newInputs.forEach(input => input.remove())
      }
    }
    
    // Aktualisiere UI sofort (ohne AJAX-Request)
    this.updateCompetencyUI(competencyId, checkbox.checked);
    
    // Speichere automatisch über das Formular (statt separater AJAX-Requests)
    // Dies ermöglicht Live-Updates mit Turbo
    this.saveProfileCompetencies();
  }

  removeCompetency(event) {
    const competencyId = event.currentTarget.dataset.competencyId;
    const profileId = this.profileIdValue || this.selectTarget?.dataset.profileId;

    // Bestaetigung VOR dem Loeschen: "Abbrechen" muss das Entfernen wirklich
    // verhindern (vorher wurde sofort per DELETE-fetch geloescht, unabhaengig
    // vom Dialog -> Klick auf Abbrechen entfernte die Kompetenz trotzdem).
    if (!window.confirm('Diese Fachkompetenz wirklich aus dem Profil entfernen?')) return;

    if (profileId && profileId !== 'new') {
      this.removeCompetencyFromProfile(competencyId, profileId);
    }
  }

  removeCompetencyFromProfile(competencyId, profileId) {
    // Finde die ProfileCompetency ID für diese Kompetenz
    const competencyElement = document.querySelector(`[data-competency-id="${competencyId}"]`);
    let profileCompetencyId = competencyElement?.dataset.profileCompetencyId;
    
    // Wenn profile_competency_id nicht direkt verfügbar ist, versuche es über versteckte Input-Felder zu finden
    if (!profileCompetencyId && competencyElement) {
      // Suche nach einem versteckten Input-Feld mit der profile_competency_id
      const hiddenIdInputs = competencyElement.querySelectorAll('input[type="hidden"][name*="profile_competencies_attributes"][name*="[id]"]');
      if (hiddenIdInputs.length > 0) {
        // Nimm die erste gefundene ID
        profileCompetencyId = hiddenIdInputs[0].value;
        // Aktualisiere das data-attribute für zukünftige Verwendung
        if (profileCompetencyId) {
          competencyElement.setAttribute('data-profile-competency-id', profileCompetencyId);
        }
      }
    }
    
    if (!profileCompetencyId) {
      this.showError('Fehler beim Entfernen der Kompetenz: ID nicht gefunden. Bitte aktualisieren Sie die Seite und versuchen Sie es erneut.');
      return;
    }
    
    const url = `/de/backend/profiles/${profileId}/profile_competencies/${profileCompetencyId}`;
    
    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (response.ok) {
        // Aktualisiere die UI - entferne Gewichtungsfeld und mache grau
        this.updateCompetencyUI(competencyId, false);
      } else {
        this.showError('Fehler beim Entfernen der Kompetenz');
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Entfernen der Kompetenz');
    });
  }

  sendRequest(url, formData, competencyId = null) {
    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => {
      if (response.ok) {
        // Nur TomSelect leeren wenn es existiert
        if (this.tomselect) {
          this.tomselect.clear();
          this.tomselect.clearOptions();
        }
        
        // Aktualisiere die Checkbox und zeige Gewichtungsfeld
        if (competencyId) {
          this.updateCompetencyUI(competencyId, true);
        }
        
        // Verarbeite Turbo Stream Response
        return response.text();
      } else {
        return response.text().then(text => {
          this.showError('Fehler beim Hinzufügen der Kompetenz: ' + response.status);
          throw new Error('Request failed');
        });
      }
    })
    .then(html => {
      if (html) {
        // Verarbeite die Turbo Stream Response
        Turbo.renderStreamMessage(html);
      }
    })
    .catch(error => {
      this.showError('Netzwerkfehler beim Hinzufügen der Kompetenz: ' + error.message);
    });
  }

  updateCompetencyUI(competencyId, isSelected) {
    const competencyElement = document.querySelector(`[data-competency-id="${competencyId}"]`);
    if (competencyElement) {
      if (isSelected) {
        competencyElement.classList.remove('opacity-50');
        // Zeige Highlight-Toggle Container
        const highlightContainer = competencyElement.querySelector('.highlight-toggle-container');
        if (highlightContainer) {
          highlightContainer.style.display = 'flex';
          // Aktualisiere das Checkbox-Attribut name mit der richtigen ID
          const checkbox = highlightContainer.querySelector('input[type="checkbox"]');
          if (checkbox && checkbox.id.startsWith('highlight_new_')) {
            // Extrahiere applicant_competency_id aus dem Element
            const applicantCompetencyId = competencyElement.getAttribute('data-applicant-competency-id');
            checkbox.name = `profile[profile_competencies_attributes][new_${applicantCompetencyId}][highlight]`;
            checkbox.id = `highlight_new_${applicantCompetencyId}`;
          }
        } else {
          // Falls Container nicht existiert, erstelle ihn
          const controlsContainer = document.createElement('div');
          controlsContainer.className = 'flex items-center space-x-3 ml-auto';
          
          // Highlight Toggle
          const highlightDiv = document.createElement('div');
          highlightDiv.className = 'flex items-center space-x-2';
          const applicantCompetencyId = competencyElement.getAttribute('data-applicant-competency-id');
          highlightDiv.innerHTML = `
            <label class="text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" 
                     name="profile[profile_competencies_attributes][new_${applicantCompetencyId}][highlight]" 
                     value="1" 
                     class="rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                     id="highlight_new_${applicantCompetencyId}">
              <span class="ml-1 text-xs text-gray-600">
                <i class="far fa-star text-gray-300"></i>
                Hervorheben
              </span>
            </label>
          `;
          
          controlsContainer.appendChild(highlightDiv);
          const mainContainer = competencyElement.querySelector('.flex.items-center.justify-between');
          if (mainContainer) {
            mainContainer.appendChild(controlsContainer);
          }
        }
      } else {
        competencyElement.classList.add('opacity-50');
        // Verstecke Highlight-Toggle Container
        const highlightContainer = competencyElement.querySelector('.highlight-toggle-container');
        if (highlightContainer) {
          highlightContainer.style.display = 'none';
        } else {
          // Entferne dynamisch erstellte Container
          const controlsContainer = competencyElement.querySelector('.flex.items-center.space-x-3.ml-auto');
          if (controlsContainer && controlsContainer.querySelector('.highlight-toggle-container, input[type="checkbox"][name*="highlight"]')) {
            controlsContainer.remove();
          }
        }
        
        // Aktualisiere Positionen nach dem Entfernen
        this.updateAllPositions();
      }
    }
  }

  updateAllPositions() {
    // Finde den competency-sortable Controller und aktualisiere Positionen
    const sortableElement = this.element.closest('[data-controller*="competency-sortable"]')
    if (sortableElement) {
      const sortableController = this.application.getControllerForElementAndIdentifier(
        sortableElement,
        'competency-sortable'
      )
      
      if (sortableController && typeof sortableController.updatePositions === 'function') {
        sortableController.updatePositions()
      }
    }
  }

  getSkillCategoryId() {
    // Versuche die Skill-Kategorie-ID zu finden oder verwende einen Standard
    return 1; // Standard Skill-Kategorie ID
  }

  showError(message) {
    // Erstelle eine temporäre Fehlermeldung
    const errorDiv = document.createElement('div');
    errorDiv.className = 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4';
    errorDiv.innerHTML = `
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm">${message}</p>
        </div>
      </div>
    `;
    
    // Füge die Fehlermeldung vor dem Controller-Element hinzu
    this.element.parentNode.insertBefore(errorDiv, this.element);
    
    // Entferne die Fehlermeldung nach 5 Sekunden
    setTimeout(() => {
      if (errorDiv.parentNode) {
        errorDiv.parentNode.removeChild(errorDiv);
      }
    }, 5000);
  }

  disconnect() {
    if (this.tomselect) {
      this.tomselect.destroy();
    }
  }
}
